import {
  createSlice,
  PayloadAction,
  createSelector,
  ActionReducerMapBuilder,
} from '@reduxjs/toolkit';
import { LoadingStatus } from '@/enums/LoadingStatus.enum';
import { DefectDataByDivision, InspectionDefectData } from '../types/InspectionDefectData.type';
import { ControlPoint } from '@/types/ControlPoint';
import { logout } from './authSlice';
import { RootState } from '../rootReducer';
import { Division } from '@/types/Division';
import { DefectCode, DefectCodes, DefectSeverity } from '@/types/DefectCategory.type';
import { CheckpointCheck } from '@/types/CheckpointCheck.type';
import { masterDataIdbService } from '@/store/idbStorage/masterDataIdbService';
import { DefectCodesLoadError } from '@/utils/defectCodesAvailability';

export interface ClientDecision {
  id: string;
  description: string;
}

export interface ImageCategory {
  templateId: string;
  imageCategoryId: string;
  imageCategoryName: string;
  displayOrder: number;
  isDefault: boolean;
}

export interface MasterDataState {
  controlPoints: ControlPoint[];
  divisions: Division[];
  clientDecisions: ClientDecision[];
  imageCategories: ImageCategory[];
  checkpointChecks: CheckpointCheck[];
  defectsByDivision: DefectDataByDivision[];
  severities: DefectSeverity[];
  loadedDefectControlPointId: string | null;
  defectsLoadError: DefectCodesLoadError | null;
  entityStatus: Record<string, LoadingStatus>;
  error?: string | null;
}

const initialState: MasterDataState = {
  controlPoints: [],
  divisions: [],
  clientDecisions: [],
  imageCategories: [],
  checkpointChecks: [],
  defectsByDivision: [],
  severities: [],
  loadedDefectControlPointId: null,
  defectsLoadError: null,
  entityStatus: {},
  error: null,
};

const masterDataSlice = createSlice({
  name: 'masterData',
  initialState,
  reducers: {
    setDefectCodesAndSeverities: (
      state: MasterDataState,
      action: PayloadAction<InspectionDefectData>
    ) => {
      // Sort defects by category description and codes by codeDescription before setting in state
      const sortedDefectsByDivision =
        action.payload?.defectsByDivision?.map((division) => ({
          ...division,
          defects: division.defects
            .map((defect: DefectCodes) => ({
              ...defect,
              codes:
                defect.codes?.sort((a: DefectCode, b: DefectCode) =>
                  a.codeDescription.localeCompare(b.codeDescription)
                ) || [],
            }))
            .sort((a, b) => a.categoryDescription.localeCompare(b.categoryDescription)),
        })) || [];

      state.defectsByDivision = sortedDefectsByDivision;
      state.severities = action.payload?.severities || [];
      state.defectsLoadError = null;
      if (action.payload?.controlPointId !== undefined) {
        state.loadedDefectControlPointId = action.payload.controlPointId ?? null;
      }
    },

    clearDefectsByDivision: (state: MasterDataState) => {
      state.defectsByDivision = [];
      state.severities = [];
      state.loadedDefectControlPointId = null;
      state.defectsLoadError = null;
      state.entityStatus = {
        ...state.entityStatus,
        defects: LoadingStatus.IDLE,
      };
    },
    setDefectsLoadError: (
      state: MasterDataState,
      action: PayloadAction<DefectCodesLoadError | null>
    ) => {
      state.defectsLoadError = action.payload;
    },

    setControlPoints: (state: MasterDataState, action: PayloadAction<ControlPoint[] | null>) => {
      state.controlPoints = action.payload || [];
    },
    setClientDecisions: (
      state: MasterDataState,
      action: PayloadAction<ClientDecision[] | null>
    ) => {
      state.clientDecisions = action.payload || [];
    },
    setDivisions: (state: MasterDataState, action: PayloadAction<Division[] | null>) => {
      state.divisions = action.payload || [];
    },
    setImageCategories: (state: MasterDataState, action: PayloadAction<ImageCategory[] | null>) => {
      const imageCategories = action.payload?.sort(
        (a: ImageCategory, b: ImageCategory) => a.displayOrder - b.displayOrder
      );
      state.imageCategories = imageCategories || [];
    },
    setCheckpointChecks: (
      state: MasterDataState,
      action: PayloadAction<CheckpointCheck[] | null>
    ) => {
      state.checkpointChecks = action.payload || [];
    },
    setEntityStatus: (
      state: MasterDataState,
      action: PayloadAction<{ entity: string; status: LoadingStatus }>
    ) => {
      state.entityStatus = {
        ...state.entityStatus,
        [action.payload.entity]: action.payload.status,
      };
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<MasterDataState>) => {
    builder.addCase(logout, () => {
      void masterDataIdbService.clearAll();
      return initialState;
    }); // Reset state on logout and clear IDB
  },
});

// Falls back to empty division ('') if specific division not found
export const selectDefectDataForDivision = createSelector(
  [(state: RootState) => state.masterData, (_state: RootState, divisionId: string) => divisionId],
  (masterData: MasterDataState, divisionId: string) => {
    if (!masterData.defectsByDivision) {
      return { defectCodes: [], defectSeverities: [] };
    }

    // Get defects for the specific division, fallback to first available division if not found
    const divisionDefects = masterData.defectsByDivision.find(
      (d: { divisionId: string }) => d.divisionId === divisionId
    );
    const fallbackDefects = masterData.defectsByDivision[0] as DefectDataByDivision;

    const selectedDefects = divisionDefects?.defects || fallbackDefects?.defects || [];

    return {
      defectCodes: selectedDefects,
      defectSeverities: masterData.severities,
    };
  }
);

export const {
  setDefectCodesAndSeverities,
  clearDefectsByDivision,
  setDefectsLoadError,
  setControlPoints,
  setClientDecisions,
  setDivisions,
  setImageCategories,
  setCheckpointChecks,
  setEntityStatus,
} = masterDataSlice.actions;
export default masterDataSlice.reducer;
